import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hiringPipelineApi, Stage } from '../services/recruitingApi';

export interface HiringPipelineCreate {
  name: string;
  description?: string;
  is_default?: boolean;
}

export interface StageCreate {
  name: string;
  position: number;
  stage_type?: string;
  color?: string;
  is_terminal?: boolean;
  auto_reject_days?: number;
  default_interview_type?: string;
  scorecard_template_id?: string;
}

export const hiringPipelineKeys = {
  all: ['ats-pipelines'] as const,
  list: () => [...hiringPipelineKeys.all, 'list'] as const,
  detail: (id: string) => [...hiringPipelineKeys.all, 'detail', id] as const,
};

export function useHiringPipelines() {
  return useQuery({
    queryKey: hiringPipelineKeys.list(),
    queryFn: () => hiringPipelineApi.list(),
  });
}

export function useHiringPipeline(id: string) {
  return useQuery({
    queryKey: hiringPipelineKeys.detail(id),
    queryFn: () => hiringPipelineApi.get(id),
    enabled: !!id,
  });
}

export function useCreateHiringPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: HiringPipelineCreate) => hiringPipelineApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.all });
    },
  });
}

export function useCreateDefaultHiringPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => hiringPipelineApi.createDefault(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.all });
    },
  });
}

export interface HiringPipelineUpdate {
  name?: string;
  description?: string | null;
  is_default?: boolean;
}

export function useUpdateHiringPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: HiringPipelineUpdate }) =>
      hiringPipelineApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.all });
    },
  });
}

export function useDeleteHiringPipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hiringPipelineApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.all });
    },
  });
}

export function useCreateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, data }: { pipelineId: string; data: StageCreate }) =>
      hiringPipelineApi.createStage(pipelineId, data),
    onSuccess: (_: Stage, { pipelineId }: { pipelineId: string; data: StageCreate }) => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.detail(pipelineId) });
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.list() });
    },
  });
}

export function useUpdateStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stageId, data }: { stageId: string; data: Partial<Stage> }) =>
      hiringPipelineApi.updateStage(stageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.all });
    },
  });
}

export function useDeleteStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stageId: string) => hiringPipelineApi.deleteStage(stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hiringPipelineKeys.all });
    },
  });
}
